import { useState, useEffect, useCallback } from 'react';
import {
  Users,
  BookOpen,
  Award,
  CheckCircle,
  Search,
  Filter,
  Loader2,
  Eye,
  Clock,
  FileText,
  Bell,
  ClipboardList,
  Download,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  ExternalLink,
  Check,
  User,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import { fetchAcademicYears } from '@/services/academicPlanningService';
import { createSafeHtml } from '@/utils/sanitize';

// Interfaces
interface GradeComponentDetail {
  code: string;
  name: string;
  gradedStudents: number;
  totalStudents: number;
  percentage: number;
}

interface ProfessorCourseActivity {
  professorId: string;
  professorName: string;
  professorFirstName: string;
  professorLastName: string;
  staffCode: string;
  email: string | null;
  moduleElementId: string;
  moduleElementName: string;
  moduleName: string;
  groupeId: string;
  groupeName: string;
  filiereCode: string;
  filiereName: string;
  semesterNumber: number;
  academicYearId: string;
  configuredGradeComponents: number;
  gradeComponentDetails: GradeComponentDetail[];
  gradeComponentProgress: number;
  totalStudents: number;
  markedSessions: number;
  passedSessions: number;
  totalSessions: number;
  attendanceProgress: number;
  awaitingSubmissions: number;
  remainingSessions: number;
  announcementsCount: number;
  syllabusUploaded: boolean;
  materialsCount: number;
  assignmentsCount: number;
  lastActivityAt: string | null;
}

interface DashboardOverview {
  totalProfessors: number;
  totalClasses: number;
  averageGradeProgress: number;
  averageAttendanceProgress: number;
}

interface AcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
}

interface SemesterOption {
  id: string;
  number: number;
  name: string;
  filiereId: string;
  filiereCode: string;
  filiereName: string;
  isCurrent: boolean;
}

interface FiliereOption {
  id: string;
  code: string;
  name: string;
  diplome: string;
}

interface AssignmentSubmission {
  id: string;
  studentId: string;
  studentCode: string;
  studentName: string;
  studentEmail: string | null;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  submittedAt: string | null;
  isLate: boolean;
  lateDays: number | null;
  score: number | null;
  feedback: string | null;
  gradedAt: string | null;
  status: 'NOT_SUBMITTED' | 'SUBMITTED' | 'GRADED';
}

interface AssignmentDetail {
  id: string;
  title: string;
  description: string | null;
  instructions: string | null;
  dueDate: string;
  maxScore: number;
  totalStudents: number;
  submissionsCount: number;
  gradedCount: number;
  notSubmittedCount: number;
  submissions: AssignmentSubmission[];
}

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

const ProfessorMonitoringPage: React.FC = () => {
  const { tokens } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);

  // Academic Years
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearIds, setSelectedAcademicYearIds] = useState<string[]>([]);
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  // Semesters
  const [semesters, setSemesters] = useState<SemesterOption[]>([]);
  const [selectedSemesterIds, setSelectedSemesterIds] = useState<string[]>([]);
  const [showSemesterDropdown, setShowSemesterDropdown] = useState(false);
  const [semestersLoaded, setSemestersLoaded] = useState(false);

  // Filières
  const [filieres, setFilieres] = useState<FiliereOption[]>([]);
  const [selectedFiliereIds, setSelectedFiliereIds] = useState<string[]>([]);
  const [showFiliereDropdown, setShowFiliereDropdown] = useState(false);

  // Diplômes
  const [diplomes, setDiplomes] = useState<string[]>([]);
  const [selectedDiplomes, setSelectedDiplomes] = useState<string[]>([]);
  const [showDiplomeDropdown, setShowDiplomeDropdown] = useState(false);

  // Data
  const [searchQuery, setSearchQuery] = useState('');
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [activities, setActivities] = useState<ProfessorCourseActivity[]>([]);

  // Modals
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewCourse, setPreviewCourse] = useState<ProfessorCourseActivity | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<'excel' | 'pdf' | null>(null);

  const accessToken = tokens?.accessToken;

  // Permission check
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.PROFESSOR_MONITORING_VIEW);

  // Load academic years
  useEffect(() => {
    const loadYears = async () => {
      if (!accessToken) return;

      try {
        const years = await fetchAcademicYears(accessToken);
        setAcademicYears(years);
        const currentYear = years.find((y: AcademicYear) => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYearIds([currentYear.id]);
        }
      } catch (error) {
        console.error('Failed to load academic years:', error);
      }
    };

    loadYears();
  }, [accessToken]);

  // Load semesters, filières and diplômes when academic years change
  useEffect(() => {
    const loadFiltersData = async () => {
      if (!accessToken || selectedAcademicYearIds.length === 0) {
        setSemesters([]);
        setFilieres([]);
        setDiplomes([]);
        setSemestersLoaded(true);
        return;
      }

      setSemestersLoaded(false);
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };

        const params = new URLSearchParams();
        params.append('academicYearIds', selectedAcademicYearIds.join(','));

        // Load semesters, filières and diplômes in parallel
        const [semestersRes, filieresRes, diplomesRes] = await Promise.all([
          fetch(`${API_BASE}/admin/professor-activities/v2/semesters?${params.toString()}`, { headers }),
          fetch(`${API_BASE}/admin/professor-activities/v2/filieres?${params.toString()}`, { headers }),
          fetch(`${API_BASE}/admin/professor-activities/v2/diplomes?${params.toString()}`, { headers }),
        ]);

        if (semestersRes.ok) {
          const data = await semestersRes.json();
          setSemesters(data);
          // Auto-select current semester(s)
          const currentSems = data.filter((s: SemesterOption) => s.isCurrent);
          if (currentSems.length > 0) {
            setSelectedSemesterIds(currentSems.map((s: SemesterOption) => s.id));
          } else if (data.length > 0) {
            // Select first semester by default
            setSelectedSemesterIds([data[0].id]);
          }
        }

        if (filieresRes.ok) {
          const data = await filieresRes.json();
          setFilieres(data);
        }

        if (diplomesRes.ok) {
          const data = await diplomesRes.json();
          setDiplomes(data);
        }
      } catch (error) {
        console.error('Failed to load filter data:', error);
      } finally {
        setSemestersLoaded(true);
      }
    };

    loadFiltersData();
  }, [accessToken, selectedAcademicYearIds]);

  // Load professor activities - wait for semesters to be loaded first
  const loadActivities = useCallback(async () => {
    if (!accessToken || selectedAcademicYearIds.length === 0 || !semestersLoaded) return;

    setIsLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };

      const params = new URLSearchParams();
      params.append('academicYearIds', selectedAcademicYearIds.join(','));
      if (selectedSemesterIds.length > 0) {
        params.append('semesterIds', selectedSemesterIds.join(','));
      }
      if (selectedFiliereIds.length > 0) {
        params.append('filiereIds', selectedFiliereIds.join(','));
      }
      if (selectedDiplomes.length > 0) {
        params.append('diplomes', selectedDiplomes.join(','));
      }

      const [overviewRes, activitiesRes] = await Promise.all([
        fetch(`${API_BASE}/admin/professor-activities/v2/overview?${params.toString()}`, { headers }),
        fetch(`${API_BASE}/admin/professor-activities/v2/professors?${params.toString()}`, { headers }),
      ]);

      if (overviewRes.ok) {
        setOverview(await overviewRes.json());
      }
      if (activitiesRes.ok) {
        setActivities(await activitiesRes.json());
      }
    } catch (error) {
      console.error('Failed to load professor activities:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedAcademicYearIds, selectedSemesterIds, selectedFiliereIds, selectedDiplomes, semestersLoaded]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Filter activities by search
  const filteredActivities = activities.filter((a) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      a.professorName.toLowerCase().includes(search) ||
      a.staffCode.toLowerCase().includes(search) ||
      a.moduleElementName.toLowerCase().includes(search) ||
      a.groupeName.toLowerCase().includes(search) ||
      a.filiereName.toLowerCase().includes(search) ||
      a.filiereCode.toLowerCase().includes(search) ||
      (a.email && a.email.toLowerCase().includes(search))
    );
  });

  // Get progress color
  const getProgressColor = (value: number): string => {
    if (value >= 80) return 'bg-green-500';
    if (value >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getProgressTextColor = (value: number): string => {
    if (value >= 80) return 'text-green-600';
    if (value >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  // Handle academic year selection
  const toggleAcademicYear = (yearId: string) => {
    setSelectedAcademicYearIds(prev => {
      if (prev.includes(yearId)) {
        // Don't allow deselecting all
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== yearId);
      } else {
        return [...prev, yearId];
      }
    });
  };

  // Handle semester selection
  const toggleSemester = (semesterId: string) => {
    setSelectedSemesterIds(prev => {
      if (prev.includes(semesterId)) {
        return prev.filter(id => id !== semesterId);
      } else {
        return [...prev, semesterId];
      }
    });
  };

  // Handle filière selection
  const toggleFiliere = (filiereId: string) => {
    setSelectedFiliereIds(prev => {
      if (prev.includes(filiereId)) {
        return prev.filter(id => id !== filiereId);
      } else {
        return [...prev, filiereId];
      }
    });
  };

  // Handle diplôme selection
  const toggleDiplome = (diplome: string) => {
    setSelectedDiplomes(prev => {
      if (prev.includes(diplome)) {
        return prev.filter(d => d !== diplome);
      } else {
        return [...prev, diplome];
      }
    });
  };

  // Get selected year names for display
  const getSelectedYearsLabel = () => {
    if (selectedAcademicYearIds.length === 0) return 'Sélectionner...';
    if (selectedAcademicYearIds.length === 1) {
      const year = academicYears.find(y => y.id === selectedAcademicYearIds[0]);
      return year?.name || 'Année';
    }
    return `${selectedAcademicYearIds.length} années`;
  };

  // Get selected semester names for display
  const getSelectedSemestersLabel = () => {
    if (selectedSemesterIds.length === 0) return 'Tous les semestres';
    if (selectedSemesterIds.length === 1) {
      const sem = semesters.find(s => s.id === selectedSemesterIds[0]);
      return sem?.name || 'Semestre';
    }
    return `${selectedSemesterIds.length} semestres`;
  };

  // Get selected filières label for display
  const getSelectedFilieresLabel = () => {
    if (selectedFiliereIds.length === 0) return 'Toutes les filières';
    if (selectedFiliereIds.length === 1) {
      const fil = filieres.find(f => f.id === selectedFiliereIds[0]);
      return fil?.name || 'Filière';
    }
    return `${selectedFiliereIds.length} filières`;
  };

  // Get selected diplômes label for display
  const getSelectedDiplomesLabel = () => {
    if (selectedDiplomes.length === 0) return 'Tous les diplômes';
    if (selectedDiplomes.length === 1) {
      return selectedDiplomes[0];
    }
    return `${selectedDiplomes.length} diplômes`;
  };

  // Handle export - separate loading state for each format
  const handleExport = async (format: 'excel' | 'pdf') => {
    if (!accessToken) return;

    setExportingFormat(format);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };

      const res = await fetch(`${API_BASE}/admin/professor-activities/v2/export`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          academicYearIds: selectedAcademicYearIds,
          semesterIds: selectedSemesterIds.length > 0 ? selectedSemesterIds : undefined,
          filiereIds: selectedFiliereIds.length > 0 ? selectedFiliereIds : undefined,
          diplomes: selectedDiplomes.length > 0 ? selectedDiplomes : undefined,
          format,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        // Trigger actual download
        const downloadUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${data.fileUrl}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setExportModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to export report:', error);
    } finally {
      setExportingFormat(null);
    }
  };

  // Open preview modal
  const openPreview = (activity: ProfessorCourseActivity) => {
    setPreviewCourse(activity);
    setPreviewModalOpen(true);
  };

  if (isLoading && !overview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Check if user has permission to view this page
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès non autorisé</h2>
        <p className="text-gray-500">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Suivi des Professeurs</h1>
              <p className="mt-1 text-sm text-gray-500">
                Surveillez l'activité des professeurs et l'avancement des cours
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Academic Year Multi-Select */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowYearDropdown(!showYearDropdown);
                    setShowSemesterDropdown(false);
                    setShowFiliereDropdown(false);
                    setShowDiplomeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{getSelectedYearsLabel()}</span>
                  {showYearDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showYearDropdown && (
                  <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    <div className="p-2">
                      {academicYears.map((year) => (
                        <label
                          key={year.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAcademicYearIds.includes(year.id)}
                            onChange={() => toggleAcademicYear(year.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">{year.name}</span>
                          {year.isCurrent && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">En cours</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Semester Multi-Select */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowSemesterDropdown(!showSemesterDropdown);
                    setShowYearDropdown(false);
                    setShowFiliereDropdown(false);
                    setShowDiplomeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{getSelectedSemestersLabel()}</span>
                  {showSemesterDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showSemesterDropdown && (
                  <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    <div className="p-2">
                      {semesters.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Aucun semestre disponible</div>
                      ) : (
                        semesters.map((sem) => (
                          <label
                            key={sem.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSemesterIds.includes(sem.id)}
                              onChange={() => toggleSemester(sem.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <span className="text-sm">{sem.name}</span>
                              <span className="text-xs text-gray-500 ml-1">({sem.filiereCode})</span>
                            </div>
                            {sem.isCurrent && (
                              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Actuel</span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Diplôme Multi-Select */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowDiplomeDropdown(!showDiplomeDropdown);
                    setShowYearDropdown(false);
                    setShowSemesterDropdown(false);
                    setShowFiliereDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  <Award className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{getSelectedDiplomesLabel()}</span>
                  {showDiplomeDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showDiplomeDropdown && (
                  <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    <div className="p-2">
                      {diplomes.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Aucun diplôme disponible</div>
                      ) : (
                        diplomes.map((diplome) => (
                          <label
                            key={diplome}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedDiplomes.includes(diplome)}
                              onChange={() => toggleDiplome(diplome)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm">{diplome}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Filière Multi-Select */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowFiliereDropdown(!showFiliereDropdown);
                    setShowYearDropdown(false);
                    setShowSemesterDropdown(false);
                    setShowDiplomeDropdown(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  <BookOpen className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">{getSelectedFilieresLabel()}</span>
                  {showFiliereDropdown ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showFiliereDropdown && (
                  <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    <div className="p-2">
                      {filieres.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Aucune filière disponible</div>
                      ) : (
                        filieres.map((filiere) => (
                          <label
                            key={filiere.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFiliereIds.includes(filiere.id)}
                              onChange={() => toggleFiliere(filiere.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm truncate block">{filiere.name}</span>
                              <span className="text-xs text-gray-500">{filiere.diplome} • {filiere.code}</span>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Export Button */}
              <button
                onClick={() => setExportModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Download className="h-4 w-4" />
                <span className="text-sm">Exporter</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showYearDropdown || showSemesterDropdown || showFiliereDropdown || showDiplomeDropdown) && (
        <div
          className="fixed inset-0 z-[5]"
          onClick={() => {
            setShowYearDropdown(false);
            setShowSemesterDropdown(false);
            setShowFiliereDropdown(false);
            setShowDiplomeDropdown(false);
          }}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Overview Cards - 4 cards only */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <Users className="h-4 w-4" />
                Professeurs
              </div>
              <div className="text-2xl font-bold text-gray-900">{overview.totalProfessors}</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <BookOpen className="h-4 w-4" />
                Cours
              </div>
              <div className="text-2xl font-bold text-gray-900">{overview.totalClasses}</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <Award className="h-4 w-4" />
                Notes
              </div>
              <div className={`text-2xl font-bold ${getProgressTextColor(overview.averageGradeProgress)}`}>
                {Math.round(overview.averageGradeProgress)}%
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <CheckCircle className="h-4 w-4" />
                Présences
              </div>
              <div className={`text-2xl font-bold ${getProgressTextColor(overview.averageAttendanceProgress)}`}>
                {Math.round(overview.averageAttendanceProgress)}%
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un professeur, cours ou groupe..."
            className="w-full max-w-md pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}

        {/* Activities Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Professeur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cours
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Présences
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span title="Séances restantes à effectuer">Restantes</span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contenu
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière activité
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredActivities.map((activity, idx) => (
                  <tr key={`${activity.professorId}-${activity.moduleElementId}-${activity.groupeId}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{activity.professorName}</div>
                        <div className="text-sm text-gray-500">{activity.staffCode}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{activity.moduleElementName}</div>
                        <div className="text-xs text-gray-500">
                          {activity.filiereName || activity.filiereCode} • {activity.groupeName} • S{activity.semesterNumber}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2" title={
                        activity.gradeComponentDetails.length > 0
                          ? activity.gradeComponentDetails.map(c => `${c.name}: ${c.percentage}%`).join('\n')
                          : 'Aucune composante configurée'
                      }>
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getProgressColor(activity.gradeComponentProgress)}`}
                            style={{ width: `${activity.gradeComponentProgress}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${getProgressTextColor(activity.gradeComponentProgress)}`}>
                          {activity.gradeComponentProgress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div
                        className="flex items-center justify-center gap-2"
                        title={`${activity.markedSessions}/${activity.passedSessions} séances marquées`}
                      >
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getProgressColor(activity.attendanceProgress)}`}
                            style={{ width: `${Math.min(activity.attendanceProgress, 100)}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${getProgressTextColor(activity.attendanceProgress)}`}>
                          {activity.attendanceProgress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {activity.remainingSessions > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          <Clock className="h-3 w-3" />
                          {activity.remainingSessions}
                        </span>
                      ) : (
                        <span className="text-sm text-green-600">Terminé</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1" title="Annonces">
                          <Bell className="h-3.5 w-3.5" />
                          {activity.announcementsCount}
                        </span>
                        <span className="flex items-center gap-1" title="Syllabus">
                          <FileText className={`h-3.5 w-3.5 ${activity.syllabusUploaded ? 'text-green-600' : 'text-gray-400'}`} />
                          {activity.syllabusUploaded ? '✓' : '-'}
                        </span>
                        <span className="flex items-center gap-1" title="Matériels">
                          <BookOpen className="h-3.5 w-3.5" />
                          {activity.materialsCount}
                        </span>
                        <span className="flex items-center gap-1" title="Devoirs">
                          <ClipboardList className="h-3.5 w-3.5" />
                          {activity.assignmentsCount}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {activity.lastActivityAt ? (
                        <span className="text-sm text-gray-500">
                          {new Date(activity.lastActivityAt).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openPreview(activity)}
                        className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50"
                        title="Voir le contenu du cours"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredActivities.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun cours trouvé</h3>
              <p className="mt-2 text-sm text-gray-500">
                {searchQuery
                  ? 'Essayez de modifier votre recherche'
                  : "Aucun professeur n'a de cours assigné pour cette période"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Course Content Preview Modal */}
      {previewModalOpen && previewCourse && (
        <CourseContentPreviewModal
          isOpen={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewCourse(null);
          }}
          course={previewCourse}
          accessToken={accessToken}
        />
      )}

      {/* Export Modal */}
      {exportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Exporter le rapport</h3>
              <button onClick={() => setExportModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Choisissez le format d'export pour le rapport de suivi des professeurs.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => handleExport('excel')}
                  disabled={exportingFormat !== null}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {exportingFormat === 'excel' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <FileText className="h-5 w-5 text-green-600" />
                  )}
                  <span>Excel (.xlsx)</span>
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={exportingFormat !== null}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {exportingFormat === 'pdf' ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <FileText className="h-5 w-5 text-red-600" />
                  )}
                  <span>PDF (.pdf)</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Course Content Preview Modal Component
interface CourseContentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  course: ProfessorCourseActivity;
  accessToken: string | undefined;
}

interface CourseContentData {
  courseInfo: {
    moduleElementName: string;
    moduleName: string;
    groupeName: string;
    professorName: string;
    filiereCode: string;
  };
  announcements: Array<{
    id: string;
    title: string;
    content: string;
    isPinned: boolean;
    createdAt: string;
  }>;
  syllabus: {
    id: string;
    fileUrl: string;
    fileName: string;
    fileType: string;
    description: string | null;
  } | null;
  chapters: Array<{
    id: string;
    title: string;
    description: string | null;
    isSection: boolean;
    materials: Array<{
      id: string;
      title: string;
      description: string | null;
      materialType: string;
      fileUrl: string | null;
      fileName: string | null;
      externalUrl: string | null;
    }>;
  }>;
  generalMaterials: Array<{
    id: string;
    title: string;
    description: string | null;
    materialType: string;
    fileUrl: string | null;
    fileName: string | null;
    externalUrl: string | null;
  }>;
  assignments: Array<{
    id: string;
    title: string;
    description: string | null;
    instructions: string | null;
    dueDate: string;
    maxScore: number;
    attachments: Array<{
      url: string;
      fileName: string;
      fileType?: string;
    }>;
    submissionsCount: number;
    gradedCount: number;
    includeInFinalGrade: boolean;
    gradeComponentCode: string | null;
  }>;
}

const CourseContentPreviewModal: React.FC<CourseContentPreviewModalProps> = ({
  isOpen: _isOpen,
  onClose,
  course,
  accessToken,
}) => {
  const [activeTab, setActiveTab] = useState<'announcements' | 'syllabus' | 'content' | 'assignments'>('announcements');
  const [isLoading, setIsLoading] = useState(true);
  const [contentData, setContentData] = useState<CourseContentData | null>(null);
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [expandedAssignments, setExpandedAssignments] = useState<Set<string>>(new Set());
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // Submissions modal states
  const [submissionsModalOpen, setSubmissionsModalOpen] = useState(false);
  const [assignmentDetail, setAssignmentDetail] = useState<AssignmentDetail | null>(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  useEffect(() => {
    if (!_isOpen || !accessToken) return;

    const loadContent = async () => {
      setIsLoading(true);
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };

        const res = await fetch(
          `${API_BASE}/admin/professor-activities/v2/course-preview/${course.professorId}/${course.moduleElementId}/${course.groupeId}?academicYearId=${course.academicYearId}`,
          { headers }
        );

        if (res.ok) {
          const data = await res.json();
          console.log('Course content loaded:', {
            syllabusUrl: data.syllabus?.fileUrl,
            materialsCount: data.chapters.reduce((sum: number, ch: any) => sum + ch.materials.length, 0) + data.generalMaterials.length,
            assignmentsCount: data.assignments.length,
          });
          setContentData(data);
        } else {
          console.error('Failed to load course content:', res.status, res.statusText);
        }
      } catch (error) {
        console.error('Failed to load course content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [_isOpen, accessToken, course]);

  const toggleChapter = (chapterId: string) => {
    setExpandedChapters(prev => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  };

  const toggleAssignment = (assignmentId: string) => {
    setExpandedAssignments(prev => {
      const next = new Set(prev);
      if (next.has(assignmentId)) {
        next.delete(assignmentId);
      } else {
        next.add(assignmentId);
      }
      return next;
    });
  };

  // Download file with authentication and proper error handling
  const handleDownload = async (fileUrl: string, fileName: string) => {
    if (!fileUrl) {
      console.error('No file URL provided');
      alert('Erreur: URL du fichier non disponible');
      return;
    }

    // Prevent multiple simultaneous downloads
    if (downloadingFile) {
      alert('Téléchargement en cours. Veuillez patienter...');
      return;
    }

    setDownloadingFile(fileName);

    try {
      // Check if it's a data URI (base64 encoded file)
      if (fileUrl.startsWith('data:')) {
        console.log('Downloading base64 file:', fileName);

        // For data URIs, directly create download link
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('Download successful:', fileName);
        setDownloadingFile(null);
        return;
      }

      // For regular file paths, fetch from server with authentication
      if (!accessToken) {
        alert('Erreur: Session expirée. Veuillez vous reconnecter.');
        setDownloadingFile(null);
        return;
      }

      // Build full URL
      const fullUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${fileUrl}`;
      console.log('Downloading from:', fullUrl);

      // Fetch with authentication
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.error('Download failed:', {
          status: response.status,
          statusText: response.statusText,
          url: fullUrl,
          fileName,
        });

        if (response.status === 404) {
          alert(`Erreur: Le fichier "${fileName}" n'existe pas ou a été supprimé.\n\nChemin: ${fileUrl}`);
        } else if (response.status === 403) {
          alert('Erreur: Vous n\'avez pas la permission d\'accéder à ce fichier.');
        } else {
          alert(`Erreur: Impossible de télécharger le fichier (Code: ${response.status})\n\nFichier: ${fileName}`);
        }
        return;
      }

      // Get the blob
      const blob = await response.blob();

      // Create a download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log('Download successful:', fileName);
    } catch (error) {
      console.error('Download error:', error);
      alert('Erreur: Impossible de télécharger le fichier. Veuillez réessayer.');
    } finally {
      setDownloadingFile(null);
    }
  };

  const getFileIcon = (fileType?: string) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('doc')) return '📝';
    if (fileType?.includes('ppt') || fileType?.includes('presentation')) return '📊';
    if (fileType?.includes('xls') || fileType?.includes('excel')) return '📈';
    if (fileType?.includes('image') || fileType?.includes('png') || fileType?.includes('jpg')) return '🖼️';
    if (fileType?.includes('video')) return '🎬';
    return '📎';
  };

  // Load assignment submissions
  const handleViewSubmissions = async (assignmentId: string) => {
    setLoadingSubmissions(true);
    setSubmissionsModalOpen(true);

    try {
      const response = await fetch(
        `${API_BASE}/admin/professor-activities/v2/assignment/${assignmentId}/submissions`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAssignmentDetail(data);
      } else {
        console.error('Failed to load submissions:', response.status);
        alert('Erreur lors du chargement des soumissions');
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      alert('Erreur lors du chargement des soumissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  if (!_isOpen) return null;

  const tabs = [
    { id: 'announcements', label: 'Annonces', icon: Bell },
    { id: 'syllabus', label: 'Syllabus', icon: FileText },
    { id: 'content', label: 'Contenu', icon: BookOpen },
    { id: 'assignments', label: 'Devoirs', icon: ClipboardList },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full mx-4 h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">{course.moduleElementName}</h3>
            <p className="text-sm text-gray-500">
              {course.professorName} • {course.groupeName} • {course.filiereCode}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : !contentData ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-gray-500">Impossible de charger le contenu</p>
            </div>
          ) : (
            <>
              {/* Announcements Tab */}
              {activeTab === 'announcements' && (
                <div className="space-y-4">
                  {contentData.announcements.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Aucune annonce</div>
                  ) : (
                    contentData.announcements.map((announcement) => (
                      <div
                        key={announcement.id}
                        className={`p-4 rounded-lg border ${
                          announcement.isPinned ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium">{announcement.title}</h4>
                          {announcement.isPinned && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Épinglé</span>
                          )}
                        </div>
                        <div
                          className="mt-2 text-sm text-gray-600 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={createSafeHtml(announcement.content)}
                        />
                        <p className="mt-2 text-xs text-gray-400">
                          {new Date(announcement.createdAt).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Syllabus Tab - Download only */}
              {activeTab === 'syllabus' && (
                <div>
                  {!contentData.syllabus ? (
                    <div className="text-center py-8 text-gray-500">Aucun syllabus publié</div>
                  ) : !contentData.syllabus.fileUrl ? (
                    <div className="text-center py-8">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-4 text-gray-500">Le syllabus existe mais le fichier n'est pas disponible</p>
                      <p className="text-xs text-gray-400 mt-1">Le professeur doit téléverser à nouveau le fichier</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getFileIcon(contentData.syllabus.fileType)}</span>
                        <div>
                          <h4 className="font-medium">{contentData.syllabus.fileName || 'Syllabus'}</h4>
                          <p className="text-xs text-gray-500">{contentData.syllabus.fileType?.toUpperCase() || 'PDF'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(contentData.syllabus!.fileUrl, contentData.syllabus!.fileName || 'syllabus.pdf')}
                        disabled={downloadingFile === (contentData.syllabus!.fileName || 'syllabus.pdf')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingFile === (contentData.syllabus!.fileName || 'syllabus.pdf') ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Téléchargement...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            Télécharger
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Content Tab - Expandable chapters */}
              {activeTab === 'content' && (
                <div className="space-y-4">
                  {contentData.chapters.length === 0 && contentData.generalMaterials.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Aucun contenu publié</div>
                  ) : (
                    <>
                      {contentData.chapters.map((chapter) => (
                        <div key={chapter.id} className="border rounded-lg overflow-hidden">
                          {/* Chapter header - Click to expand/collapse */}
                          <button
                            onClick={() => toggleChapter(chapter.id)}
                            className={`w-full flex items-center justify-between px-4 py-3 font-medium ${
                              chapter.isSection ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                            } hover:opacity-90 transition-opacity`}
                          >
                            <span>{chapter.title}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs opacity-70">
                                {chapter.materials.length} fichier{chapter.materials.length > 1 ? 's' : ''}
                              </span>
                              {expandedChapters.has(chapter.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </div>
                          </button>

                          {/* Expanded materials */}
                          {expandedChapters.has(chapter.id) && chapter.materials.length > 0 && (
                            <div className="divide-y bg-white">
                              {chapter.materials.map((material) => (
                                <div key={material.id} className="px-4 py-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg">{getFileIcon(material.materialType)}</span>
                                      <div>
                                        <span className="text-sm font-medium">{material.title}</span>
                                        {material.description && (
                                          <p className="text-xs text-gray-500 mt-0.5">{material.description}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {material.fileUrl && (
                                        <button
                                          onClick={() => handleDownload(material.fileUrl!, material.fileName || material.title)}
                                          disabled={downloadingFile === (material.fileName || material.title)}
                                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {downloadingFile === (material.fileName || material.title) ? (
                                            <>
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              Téléch...
                                            </>
                                          ) : (
                                            <>
                                              <Download className="h-3 w-3" />
                                              Télécharger
                                            </>
                                          )}
                                        </button>
                                      )}
                                      {material.externalUrl && (
                                        <a
                                          href={material.externalUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50"
                                        >
                                          <ExternalLink className="h-3 w-3" />
                                          Lien externe
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* General materials */}
                      {contentData.generalMaterials.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="px-4 py-3 font-medium bg-gray-50">Ressources générales</div>
                          <div className="divide-y">
                            {contentData.generalMaterials.map((material) => (
                              <div key={material.id} className="px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="text-lg">{getFileIcon(material.materialType)}</span>
                                  <span className="text-sm">{material.title}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {material.fileUrl && (
                                    <button
                                      onClick={() => handleDownload(material.fileUrl!, material.fileName || material.title)}
                                      disabled={downloadingFile === (material.fileName || material.title)}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {downloadingFile === (material.fileName || material.title) ? (
                                        <>
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                          Téléch...
                                        </>
                                      ) : (
                                        <>
                                          <Download className="h-3 w-3" />
                                          Télécharger
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Assignments Tab - Expandable */}
              {activeTab === 'assignments' && (
                <div className="space-y-4">
                  {contentData.assignments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Aucun devoir</div>
                  ) : (
                    contentData.assignments.map((assignment) => (
                      <div key={assignment.id} className="border rounded-lg overflow-hidden">
                        {/* Assignment header - Click to expand/collapse */}
                        <button
                          onClick={() => toggleAssignment(assignment.id)}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium">{assignment.title}</h4>
                              {new Date(assignment.dueDate) < new Date() && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Expiré</span>
                              )}
                              {assignment.includeInFinalGrade && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                                  <Check className="h-3 w-3" />
                                  Noté
                                </span>
                              )}
                              {assignment.gradeComponentCode && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                  {assignment.gradeComponentCode}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(assignment.dueDate).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </span>
                              <span>{assignment.maxScore} pts</span>
                              <span>{assignment.gradedCount}/{assignment.submissionsCount} notés</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {assignment.attachments && assignment.attachments.length > 0 && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {assignment.attachments.length} pièce{assignment.attachments.length > 1 ? 's' : ''} jointe{assignment.attachments.length > 1 ? 's' : ''}
                              </span>
                            )}
                            {expandedAssignments.has(assignment.id) ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </div>
                        </button>

                        {/* Expanded content */}
                        {expandedAssignments.has(assignment.id) && (
                          <div className="p-4 border-t bg-white space-y-4">
                            {/* Description */}
                            {assignment.description && (
                              <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-1">Description</h5>
                                <p className="text-sm text-gray-600">{assignment.description}</p>
                              </div>
                            )}

                            {/* Instructions */}
                            {assignment.instructions && (
                              <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-1">Instructions</h5>
                                <div
                                  className="text-sm text-gray-600 prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={createSafeHtml(assignment.instructions)}
                                />
                              </div>
                            )}

                            {/* Grade Info */}
                            {(assignment.includeInFinalGrade || assignment.gradeComponentCode) && (
                              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                <h5 className="text-sm font-medium text-green-800 mb-1">Information de notation</h5>
                                <p className="text-sm text-green-700">
                                  {assignment.includeInFinalGrade
                                    ? `Ce devoir est compté dans la note finale${assignment.gradeComponentCode ? ` (${assignment.gradeComponentCode})` : ''}.`
                                    : `Composante: ${assignment.gradeComponentCode}`
                                  }
                                </p>
                              </div>
                            )}

                            {/* Attachments */}
                            {assignment.attachments && assignment.attachments.length > 0 && (
                              <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Pièces jointes</h5>
                                <div className="space-y-2">
                                  {assignment.attachments.map((attachment, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className="text-lg">{getFileIcon(attachment.fileType)}</span>
                                        <span className="text-sm">{attachment.fileName}</span>
                                      </div>
                                      <button
                                        onClick={() => handleDownload(attachment.url, attachment.fileName)}
                                        disabled={downloadingFile === attachment.fileName}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {downloadingFile === attachment.fileName ? (
                                          <>
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                            Téléch...
                                          </>
                                        ) : (
                                          <>
                                            <Download className="h-3 w-3" />
                                            Télécharger
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {(!assignment.attachments || assignment.attachments.length === 0) && !assignment.description && !assignment.instructions && (
                              <p className="text-sm text-gray-500 italic">Pas de détails supplémentaires</p>
                            )}

                            {/* View Submissions Button */}
                            <div className="pt-3 border-t">
                              <button
                                onClick={() => handleViewSubmissions(assignment.id)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                              >
                                <Users className="h-4 w-4" />
                                Voir les soumissions ({assignment.submissionsCount}/{assignment.gradedCount} soumis/notés)
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* Submissions Modal */}
      {submissionsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-5xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-xl font-semibold">
                  {assignmentDetail?.title || 'Chargement...'}
                </h3>
                {assignmentDetail && (
                  <p className="text-sm text-gray-600 mt-1">
                    {assignmentDetail.submissionsCount} soumis · {assignmentDetail.gradedCount} notés · {assignmentDetail.notSubmittedCount} non soumis
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setSubmissionsModalOpen(false);
                  setAssignmentDetail(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingSubmissions ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              ) : assignmentDetail ? (
                <div className="space-y-2">
                  {assignmentDetail.submissions.map((submission) => (
                    <div
                      key={submission.id}
                      className={`p-4 rounded-lg border-2 ${
                        submission.status === 'NOT_SUBMITTED'
                          ? 'bg-red-50 border-red-200'
                          : submission.status === 'GRADED'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {/* Student Info */}
                        <div className="flex items-center gap-3 flex-1">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              submission.status === 'NOT_SUBMITTED'
                                ? 'bg-red-200 text-red-700'
                                : submission.status === 'GRADED'
                                ? 'bg-green-200 text-green-700'
                                : 'bg-blue-200 text-blue-700'
                            }`}
                          >
                            <User className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{submission.studentName}</p>
                              <span className="text-xs text-gray-500">({submission.studentCode})</span>
                              {submission.status === 'NOT_SUBMITTED' && (
                                <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded flex items-center gap-1">
                                  <XCircle className="h-3 w-3" />
                                  Non soumis
                                </span>
                              )}
                              {submission.status === 'SUBMITTED' && (
                                <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  En attente
                                </span>
                              )}
                              {submission.status === 'GRADED' && (
                                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Noté
                                </span>
                              )}
                              {submission.isLate && submission.status !== 'NOT_SUBMITTED' && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                  En retard ({submission.lateDays}j)
                                </span>
                              )}
                            </div>
                            {submission.submittedAt && (
                              <p className="text-xs text-gray-600 mt-0.5">
                                Soumis le {new Date(submission.submittedAt).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            )}
                            {submission.score !== null && (
                              <p className="text-sm font-semibold text-green-700 mt-1">
                                Note: {submission.score}/{assignmentDetail.maxScore}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Download Button */}
                        {submission.fileUrl && submission.fileName && (
                          <button
                            onClick={() => handleDownload(submission.fileUrl!, submission.fileName!)}
                            disabled={downloadingFile === submission.fileName}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloadingFile === submission.fileName ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Téléchargement...
                              </>
                            ) : (
                              <>
                                <Download className="h-4 w-4" />
                                Télécharger
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Aucune donnée disponible
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => {
                  setSubmissionsModalOpen(false);
                  setAssignmentDetail(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessorMonitoringPage;
